import { ArrowDown, ArrowUp, ArrowUpDown, CalendarDays, ExternalLink, MoreHorizontal, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../shared/format'
import { STAGES, type Customer, type Stage } from '../types'
import { useCrm } from '../ui'
import { CategoryBadge } from '../categories'
import { CustomerAvatar, StageBadge } from './CrmBits'

export type SortKey = 'name' | 'stage' | 'owner' | 'lastMeeting' | 'next' | 'balance'
type Props = {
  rows: Customer[]
  sort: { key: SortKey; dir: 1 | -1 }
  onSort: (k: SortKey) => void
  canWrite: boolean
  onEdit: (c: Customer) => void
  onDelete: (c: Customer) => void
  onStage: (c: Customer, s: Stage) => void
}

export function CustomerTable({ rows, sort, onSort, canWrite, onEdit, onDelete, onStage }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { staffName } = useCrm()

  const menu = (c: Customer) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-8 text-muted-foreground" aria-label={t('common.rowActions', { name: c.name })} onClick={(e) => e.stopPropagation()}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onSelect={() => navigate(`/customers/${c.id}`)}>
          <ExternalLink />
          {t('crm.openDetail')}
        </DropdownMenuItem>
        {canWrite ? (
          <>
            <DropdownMenuItem onSelect={() => onEdit(c)}>
              <Pencil />
              {t('common.edit')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t('crm.changeStage')}</DropdownMenuLabel>
            {STAGES.filter((s) => s !== c.stage).map((s) => (
              <DropdownMenuItem key={s} onSelect={() => onStage(c, s)}>
                {t(`crm.stage.${s}`)}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onDelete(c)} className="text-destructive [&_svg]:text-destructive">
              <Trash2 />
              {t('common.delete')}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const next = (c: Customer) =>
    c.nextAction ? (
      <span className={cn('flex min-w-0 items-start gap-1.5 text-sm', c.nextAction.overdue && 'text-destructive')}>
        <CalendarDays className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span className="min-w-0">
          <span className="block truncate">{c.nextAction.text}</span>
          <span className="block text-xs tabular-nums opacity-80">{fmtDate(c.nextAction.date)}</span>
        </span>
      </span>
    ) : (
      <span className="text-sm text-muted-foreground">—</span>
    )
  const balance = (c: Customer) => <span className={cn('font-semibold tabular-nums', Number(c.balance) > 0 ? 'text-destructive' : 'text-muted-foreground')}>{fmtMoney(c.balance)}</span>
  const head = (k: SortKey, label: string, className?: string) => {
    const Icon = sort.key !== k ? ArrowUpDown : sort.dir === 1 ? ArrowUp : ArrowDown
    return (
      <TH className={className} aria-sort={sort.key === k ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
        <button type="button" onClick={() => onSort(k)} className="inline-flex items-center gap-1 uppercase hover:text-foreground">
          {label}
          <Icon className="size-3" aria-hidden />
        </button>
      </TH>
    )
  }

  return (
    <div className="@container">
      <ul className="divide-y @4xl:hidden">
        {rows.map((c) => (
          <li key={c.id} className="space-y-2 p-4">
            <div className="flex items-start gap-3">
              <CustomerAvatar name={c.name} />
              <Link to={`/customers/${c.id}`} className="min-w-0 flex-1">
                <span className="block truncate font-semibold hover:text-primary">{c.name}</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {c.district}, {c.city} · {c.contactName}
                </span>
              </Link>
              {menu(c)}
            </div>
            <div className="flex flex-wrap items-center gap-2 pl-[3.25rem] text-xs text-muted-foreground">
              <StageBadge stage={c.stage} />
              <CategoryBadge categoryId={c.categoryId} />
              <span>{staffName(c.ownerId)}</span>
              {c.lastMeeting ? <span>· {t('crm.lastMeetingShort', { date: fmtDate(c.lastMeeting) })}</span> : null}
            </div>
            <div className="flex items-end justify-between gap-3 pl-[3.25rem]">
              {next(c)}
              {balance(c)}
            </div>
          </li>
        ))}
      </ul>
      <div className="hidden @4xl:block">
        <Table>
          <THead>
            <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
              {head('name', t('crm.cols.company'))}
              <TH className="hidden @5xl:table-cell">{t('crm.cols.contact')}</TH>
              {head('stage', t('crm.cols.stage'))}
              {head('owner', t('crm.cols.owner'))}
              {head('lastMeeting', t('crm.cols.lastMeeting'), 'hidden @6xl:table-cell')}
              {head('next', t('crm.cols.next'))}
              {head('balance', t('crm.cols.balance'), 'text-right')}
              <TH className="w-12" aria-label={t('crm.cols.actions')} />
            </TR>
          </THead>
          <TBody>
            {rows.map((c) => (
              <TR key={c.id} className="cursor-pointer" onClick={() => navigate(`/customers/${c.id}`)}>
                <TD>
                  <div className="flex items-center gap-3">
                    <CustomerAvatar name={c.name} className="size-9 text-xs" />
                    <div className="min-w-0">
                      <Link to={`/customers/${c.id}`} onClick={(e) => e.stopPropagation()} className="block max-w-56 truncate font-semibold hover:text-primary">
                        {c.name}
                      </Link>
                      <p className="max-w-56 truncate text-xs text-muted-foreground">
                        {c.district}, {c.city}
                      </p>
                    </div>
                  </div>
                </TD>
                <TD className="hidden @5xl:table-cell">
                  <p className="text-sm">{c.contactName}</p>
                  <p className="text-xs text-muted-foreground">{c.contactTitle}</p>
                </TD>
                <TD>
                  <span className="flex flex-wrap items-center gap-1">
                    <StageBadge stage={c.stage} />
                    <CategoryBadge categoryId={c.categoryId} />
                  </span>
                </TD>
                <TD>
                  <span className="flex items-center gap-2 whitespace-nowrap text-sm">
                    <Avatar name={staffName(c.ownerId)} className="size-7 [&>span]:text-[10px]" />
                    {staffName(c.ownerId)}
                  </span>
                </TD>
                <TD className="hidden whitespace-nowrap text-sm tabular-nums @6xl:table-cell">{c.lastMeeting ? fmtDate(c.lastMeeting) : '—'}</TD>
                <TD className="max-w-56">{next(c)}</TD>
                <TD className="text-right">{balance(c)}</TD>
                <TD className="text-right">{menu(c)}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  )
}
